// Page Mon Équipe - Gestion des membres et invitations
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, Settings, Copy, RefreshCw,
  Crown, Shield, User, Trash2, Check, X,
  Mail, MoreVertical, ChevronRight, Loader2,
  ArrowLeft, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = {
  superadmin: { label: 'Superadmin', icon: Crown, color: '#E8650A', description: 'Propriétaire du compte - Accès total' },
  admin: { label: 'Admin', icon: Crown, color: '#D97706', description: 'Accès complet à tout' },
  commercial: { label: 'Commercial', icon: Shield, color: '#2563EB', description: 'CRM, prospects, devis, pipeline' },
  marketing: { label: 'Marketing', icon: Shield, color: '#7C3AED', description: 'Réseaux sociaux, communication' },
  administratif: { label: 'Administratif', icon: Shield, color: '#059669', description: 'Factures, documents, comptabilité' },
  service_client: { label: 'Service Client', icon: Shield, color: '#DC2626', description: 'Tickets, messages, support' },
  chef_chantier: { label: 'Chef de Chantier', icon: Shield, color: '#EA580C', description: 'Chantiers, planning, équipes terrain' },
  manager: { label: 'Manager', icon: Shield, color: '#1E3A5F', description: 'Gestion équipe' },
  member: { label: 'Membre', icon: User, color: '#6B7280', description: 'Accès standard' }
};

const PERMISSIONS = [
  { id: 'crm', label: 'CRM & Clients', roles: ['commercial', 'admin', 'superadmin'] },
  { id: 'prospects', label: 'Prospects & Pipeline', roles: ['commercial', 'admin', 'superadmin'] },
  { id: 'devis', label: 'Devis & Factures', roles: ['commercial', 'administratif', 'admin', 'superadmin'] },
  { id: 'chantiers', label: 'Chantiers', roles: ['chef_chantier', 'admin', 'superadmin'] },
  { id: 'reseaux_sociaux', label: 'Réseaux Sociaux', roles: ['marketing', 'admin', 'superadmin'] },
  { id: 'blog', label: 'Blog & Articles', roles: ['marketing', 'admin', 'superadmin'] },
  { id: 'communication', label: 'Communication', roles: ['marketing', 'admin', 'superadmin'] },
  { id: 'tickets', label: 'Tickets Support', roles: ['service_client', 'admin', 'superadmin'] },
  { id: 'messages', label: 'Messages Clients', roles: ['service_client', 'commercial', 'admin', 'superadmin'] },
  { id: 'tasks', label: 'Tâches', roles: ['all'] },
  { id: 'chat', label: 'Tchat Équipe', roles: ['all'] },
  { id: 'feed', label: 'Feed Équipe', roles: ['all'] },
  { id: 'documents', label: 'Documents', roles: ['administratif', 'admin', 'superadmin'] },
  { id: 'agenda', label: 'Agenda', roles: ['all'] },
  { id: 'boutique', label: 'Gestion Boutique', roles: ['admin', 'superadmin'] },
  { id: 'catalogue', label: 'Catalogue Produits', roles: ['admin', 'superadmin'] },
  { id: 'analytics', label: 'Statistiques', roles: ['admin', 'superadmin'] },
  { id: 'parametres', label: 'Paramètres Compte', roles: ['superadmin'] },
  { id: 'supprimer_compte', label: 'Supprimer le Compte', roles: ['superadmin'] }
];

function CreateEquipeModal({ isOpen, onClose, newTeamName, setNewTeamName, newTeamDesc, setNewTeamDesc, creating, onSubmit }) {
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isOpen || !isMobile) { setKeyboardHeight(0); return; }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  if (!isOpen) return null;

  const formFields = (
    <>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Nom de l'équipe *</label>
        <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
          placeholder="Ex: Équipe Chantiers"
          className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400" />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</label>
        <textarea value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)} rows={4}
          placeholder="Description optionnelle..."
          className="w-full p-4 rounded-xl border text-[15px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none" />
      </div>
    </>
  );

  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        <header className="flex items-center gap-2 px-3 border-b border-gray-100"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
          <button onClick={onClose} className="p-1 text-gray-500"><ArrowLeft size={22} /></button>
          <h1 className="text-[15px] font-bold text-gray-900">Créer une équipe</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{formFields}</div>
        <div className="p-4 border-t border-gray-100 bg-white"
          style={{
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}>
          <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200">
            <Sparkles size={16} />Créer avec Abracada
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
            <button onClick={onSubmit} disabled={!newTeamName.trim() || creating}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
              {creating ? <Loader2 size={18} className="animate-spin" /> : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Créer une équipe</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">{formFields}</div>
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
          <button onClick={onSubmit} disabled={!newTeamName.trim() || creating}
            className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
            {creating ? <Loader2 size={18} className="animate-spin" /> : "Créer l'équipe"}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

export default function EquipeProPage() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
        if (data.length > 0 && !selectedTeam) {
          fetchTeamDetails(data[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement équipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${teamId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedTeam(data);
      }
    } catch (error) {
      console.error('Erreur chargement détails:', error);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/pro/teams`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newTeamName, description: newTeamDesc })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Équipe créée !');
        setShowCreateModal(false);
        setNewTeamName('');
        setNewTeamDesc('');
        fetchTeams();
        fetchTeamDetails(data.team_id);
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const copyInviteLink = () => {
    if (!selectedTeam) return;
    const link = `${window.location.origin}/rejoindre-equipe/${selectedTeam.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };

  const regenerateInvite = async () => {
    if (!selectedTeam) return;
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/regenerate-invite`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTeam({ ...selectedTeam, invite_code: data.invite_code });
        toast.success('Nouveau lien généré !');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateMemberRole = async (memberId, newRole, newPermissions) => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/members/${memberId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ role: newRole, permissions: newPermissions })
      });
      if (res.ok) {
        toast.success('Membre mis à jour');
        fetchTeamDetails(selectedTeam.id);
        setShowMemberModal(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const removeMember = async (memberId) => {
    if (!confirm('Retirer ce membre de l\'équipe ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/members/${memberId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Membre retiré');
        fetchTeamDetails(selectedTeam.id);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8650A]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Mon Équipe</h1>
          <p className="text-gray-500 text-sm">Gère tes collaborateurs et leurs accès</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors"
        >
          <UserPlus size={18} />
          <span>Nouvelle équipe</span>
        </button>
      </div>

      {teams.length === 0 ? (
        /* Empty state */
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <Users className="w-8 h-8 text-[#1E3A5F]" />
          </div>
          <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Aucune équipe</h3>
          <p className="text-gray-500 mb-4">Crée ta première équipe pour collaborer avec tes salariés</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors"
          >
            Créer une équipe
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des équipes */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-[#1E3A5F] mb-3">Mes équipes</h3>
            <div className="space-y-2">
              {teams.map(team => (
                <button
                  key={team.id}
                  onClick={() => fetchTeamDetails(team.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center justify-between ${
                    selectedTeam?.id === team.id 
                      ? 'bg-[#1E3A5F] text-white' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div>
                    <p className="font-medium">{team.name}</p>
                    <p className={`text-sm ${selectedTeam?.id === team.id ? 'text-white/70' : 'text-gray-500'}`}>
                      {team.member_count} membre{team.member_count > 1 ? 's' : ''}
                    </p>
                  </div>
                  <ChevronRight size={18} className={selectedTeam?.id === team.id ? 'text-white' : 'text-gray-400'} />
                </button>
              ))}
            </div>
          </div>

          {/* Détails de l'équipe sélectionnée */}
          {selectedTeam && (
            <div className="lg:col-span-2 space-y-4">
              {/* Invitation */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                  <UserPlus size={18} />
                  Inviter des membres
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Partage ce lien pour inviter des collaborateurs. <strong>1 mois gratuit</strong>, puis 4,99€/mois par membre.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/rejoindre-equipe/${selectedTeam.invite_code}`}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                  />
                  <button
                    onClick={copyInviteLink}
                    className="px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#152d4a] transition-colors"
                    title="Copier"
                  >
                    <Copy size={18} />
                  </button>
                  <button
                    onClick={regenerateInvite}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Nouveau lien"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>

              {/* Liste des membres */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                  <Users size={18} />
                  Membres ({selectedTeam.members?.length || 0})
                </h3>
                <div className="space-y-2">
                  {selectedTeam.members?.map(member => {
                    const RoleIcon = ROLES[member.role]?.icon || User;
                    const roleInfo = ROLES[member.role] || ROLES.member;
                    const isOwner = member.user_id === selectedTeam.owner_id;
                    
                    return (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-medium">
                            {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-[#1E3A5F] flex items-center gap-2">
                              {member.user?.name || 'Utilisateur'}
                              {isOwner && (
                                <span className="text-xs bg-[#E8650A] text-white px-2 py-0.5 rounded-full">
                                  Propriétaire
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <RoleIcon size={12} style={{ color: roleInfo.color }} />
                              {roleInfo.label}
                              {member.billing_status === 'trial' && (
                                <span className="ml-2 text-xs text-[#E8650A]">• Essai gratuit</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {!isOwner && (selectedTeam.my_role === 'admin' || selectedTeam.my_role === 'superadmin') && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setShowMemberModal(member)}
                              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Settings size={16} className="text-gray-500" />
                            </button>
                            <button
                              onClick={() => removeMember(member.id)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              title="Retirer"
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tarification */}
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] rounded-xl p-4 text-white">
                <h3 className="font-semibold mb-2">💰 Tarification équipe</h3>
                <ul className="text-sm space-y-1 text-white/90">
                  <li>• <strong>1er mois gratuit</strong> pour chaque nouveau membre</li>
                  <li>• Puis <strong>4,99€/mois</strong> par membre</li>
                  <li>• Superpouvoirs Assistant disponibles en option</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal création équipe */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateEquipeModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            newTeamName={newTeamName}
            setNewTeamName={setNewTeamName}
            newTeamDesc={newTeamDesc}
            setNewTeamDesc={setNewTeamDesc}
            creating={creating}
            onSubmit={createTeam}
          />
        )}
      </AnimatePresence>

      {/* Modal modification membre */}
      {showMemberModal && (
        <MemberEditModal
          member={showMemberModal}
          onClose={() => setShowMemberModal(null)}
          onSave={updateMemberRole}
        />
      )}
    </div>
  );
}

// Fonction pour obtenir les permissions par défaut selon le rôle
const getDefaultPermissions = (roleKey) => {
  if (roleKey === 'admin' || roleKey === 'superadmin') {
    return PERMISSIONS.map(p => p.id);
  }
  return PERMISSIONS
    .filter(p => p.roles.includes('all') || p.roles.includes(roleKey))
    .map(p => p.id);
};

// Composant modal édition membre
function MemberEditModal({ member, onClose, onSave }) {
  const [role, setRole] = useState(member.role);
  const [permissions, setPermissions] = useState(member.permissions || getDefaultPermissions(member.role));

  // Mettre à jour les permissions par défaut quand le rôle change
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setPermissions(getDefaultPermissions(newRole));
  };

  const togglePermission = (permId) => {
    if (permissions.includes(permId)) {
      setPermissions(permissions.filter(p => p !== permId));
    } else {
      setPermissions([...permissions, permId]);
    }
  };

  // Ne pas permettre de modifier un superadmin
  const isSuperadmin = member.role === 'superadmin';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">
          Modifier {member.user?.name || 'Membre'}
        </h3>
        
        {isSuperadmin ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-amber-800 text-sm">
              <strong>⚠️ Propriétaire du compte</strong> - Ce compte ne peut pas être modifié.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Rôle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
              <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto">
                {Object.entries(ROLES).filter(([key]) => key !== 'superadmin').map(([key, info]) => {
                  const Icon = info.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => handleRoleChange(key)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        role === key 
                          ? 'border-[#E8650A] bg-[#E8650A]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={20} style={{ color: info.color }} />
                      <div className="text-left flex-1">
                        <p className="font-medium">{info.label}</p>
                        <p className="text-xs text-gray-500">{info.description}</p>
                      </div>
                      {role === key && <Check size={18} className="text-[#E8650A]" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Permissions - masquées pour admin (accès complet) */}
            {role !== 'admin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions <span className="text-gray-400 font-normal">(pré-remplies selon le rôle)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PERMISSIONS.filter(p => p.id !== 'supprimer_compte').map(perm => {
                    const isIncluded = permissions.includes(perm.id);
                    const isRecommended = perm.roles.includes('all') || perm.roles.includes(role);
                    return (
                      <button
                        key={perm.id}
                        onClick={() => togglePermission(perm.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-sm transition-colors ${
                          isIncluded
                            ? 'border-[#1E3A5F] bg-[#1E3A5F]/5 text-[#1E3A5F]'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {isIncluded ? (
                          <Check size={14} className="text-[#1E3A5F]" />
                        ) : (
                          <div className="w-3.5 h-3.5 border border-gray-300 rounded" />
                        )}
                        <span className="flex-1 text-left">{perm.label}</span>
                        {isRecommended && !isIncluded && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 px-1 rounded">suggéré</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          {!isSuperadmin && (
            <button
              onClick={() => onSave(member.id, role, role === 'admin' ? PERMISSIONS.map(p => p.id) : permissions)}
              className="px-4 py-2 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors"
            >
              Enregistrer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
